import { useRef, useState } from "react";
import { Upload, X } from "lucide-react";

/**
 * RefSlot — compact reference upload tile used in the Studio sidebar.
 *
 * Self-contained: owns its hidden <input>, drag-drop hover state, and a
 * preview/clear surface. Drop a `File` via `onPick(file)` and the parent
 * decides what to do (preview URL, server upload, etc.).
 */
export default function RefSlot({ testId, label, hint, preview, onPick, onClear }) {
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setHover(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onPick(f);
  };

  return (
    <div
      data-testid={testId}
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={handleDrop}
      className="relative"
    >
      {preview ? (
        <div className="relative rounded-lg border border-[#D4AF37]/35 overflow-hidden bg-[#141414] group">
          <img src={preview} alt={label} className="w-full h-24 object-cover" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-2 py-1.5">
            <div className="text-[10px] font-medium text-[#D4AF37] uppercase tracking-[0.18em] leading-tight">{label}</div>
          </div>
          <button
            type="button"
            onClick={onClear}
            data-testid={`${testId}-clear`}
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 border border-white/15 text-white/70 hover:text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testId}-pick`}
          className={`w-full h-24 rounded-lg border border-dashed transition flex flex-col items-center justify-center gap-1 ${
            hover
              ? "border-[#D4AF37]/60 bg-[#1A1408]"
              : "border-white/12 bg-[#0E0E0E] hover:border-[#D4AF37]/40 hover:bg-[#141414]"
          }`}
          title={`${label} reference — ${hint}`}
        >
          <Upload className="w-3.5 h-3.5 text-white/55" />
          <div className="text-[11px] text-white/75 font-medium leading-none">{label}</div>
          <div className="text-[9px] uppercase tracking-[0.18em] text-white/35 leading-none">{hint}</div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
